import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { createCheckout } from "@/lib/api";
import { toast } from "sonner";

const fields = [
  { name: "full_name", label: "Full name", col: 2 },
  { name: "address", label: "Street address", col: 2 },
  { name: "city", label: "City", col: 1 },
  { name: "state", label: "State", col: 1 },
  { name: "zip_code", label: "ZIP code", col: 1 },
];

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: user?.name || "", address: "", city: "", state: "", zip_code: "" });
  const [submitting, setSubmitting] = useState(false);
  const shipping = subtotal >= 250 ? 0 : 15;
  const total = subtotal + shipping;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your cart is empty");
    for (const f of fields) {
      if (!form[f.name].trim()) return toast.error(`Please enter your ${f.label.toLowerCase()}`);
    }
    setSubmitting(true);
    try {
      const { url } = await createCheckout({
        ...form,
        origin_url: window.location.origin,
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
      });
      window.location.href = url; // redirect to Stripe Checkout
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="pt-[140px] pb-40 text-center px-5">
        <p className="font-display text-3xl text-ink">Your cart is empty</p>
        <p className="text-stone mt-3">Add a peptide protocol before checking out.</p>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-forest text-[#14100F] px-6 py-3 text-sm font-semibold hover:bg-forest-dark transition-colors">
          Explore peptides
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="bg-bone pt-[72px] min-h-screen">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-stone hover:text-ink transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid lg:grid-cols-[1.2fr_0.9fr] gap-12">
          <div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight text-ink leading-[1.05]">Checkout</h1>
            <p className="text-stone mt-3">
              Shipping to <span className="text-ink font-semibold">{user?.email}</span>. A licensed provider reviews every order.
            </p>

            <form onSubmit={submit} className="mt-8 grid grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.name} className={f.col === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}>
                  <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone">{f.label}</label>
                  <input
                    data-testid={`checkout-${f.name}`}
                    type="text"
                    value={form[f.name]}
                    onChange={(e) => update(f.name, e.target.value)}
                    className="mt-2 w-full bg-bone border border-line rounded-lg px-4 py-3 text-ink focus:outline-none focus:ring-1 focus:ring-forest"
                  />
                </div>
              ))}
              <button
                data-testid="place-order-btn"
                type="submit"
                disabled={submitting}
                className="col-span-2 mt-2 rounded-full bg-forest text-[#14100F] py-4 text-base font-semibold hover:bg-forest-dark active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <Lock className="w-4 h-4" strokeWidth={1.75} />
                {submitting ? "Redirecting to secure checkout…" : `Pay with card · $${total.toFixed(2)}`}
              </button>
              <p className="col-span-2 text-xs text-stone text-center">
                Payments are processed securely by Stripe. Use test card 4242 4242 4242 4242.
              </p>
            </form>
          </div>

          <div className="lg:sticky lg:top-24 self-start">
            <div className="rounded-2xl border border-line bg-sand p-6">
              <h2 className="font-display text-2xl text-ink mb-5">Order summary</h2>
              <div className="space-y-4">
                {items.map((i) => (
                  <div key={i.slug} className="flex gap-3 items-center">
                    <div className="w-14 h-16 rounded-lg bg-bone overflow-hidden flex-shrink-0 relative">
                      <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                      <span className="absolute -top-1.5 -right-1.5 bg-forest text-[#14100F] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{i.quantity}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-ink leading-tight">{i.name}</p>
                    </div>
                    <p className="text-sm font-semibold text-ink">${(i.price * i.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-line mt-5 pt-5 space-y-2.5 text-sm">
                <div className="flex justify-between text-stone"><span>Subtotal</span><span className="text-ink font-semibold">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-stone"><span>Shipping</span><span className="text-ink font-semibold">{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
                <div className="flex justify-between pt-2 border-t border-line text-base"><span className="font-semibold text-ink">Total</span><span className="font-display text-xl text-ink" data-testid="checkout-total">${total.toFixed(2)}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
